/**
 * What a Stripe subscription status means to the member looking at it.
 *
 * <p>Pure on purpose. `SubscriptionDetails.tsx` reaches the shared axios instance, and this app's
 * Jest (CRA 5 / jest 27) cannot import axios 1.7.8 at all — its ESM `exports` map defeats the
 * resolver — so any predicate that is worth a test has to live in a module that imports nothing.
 *
 * <p>The distinction every rule here turns on is whether the member has PAID for the period they
 * are in. `active` and `trialing` have; `past_due`, `unpaid` and `incomplete` are exactly the
 * states where a charge did not go through, and since the monthly-only cutover the first failed
 * charge also revokes premium. Until this release the server only ever reported `active`, so those
 * members saw "Momentálne nemáte aktivované členstvo." and were offered nothing at all — no card to
 * replace, no way to stop Stripe retrying.
 */

/** A charge did not go through and Stripe is still retrying the card. */
export const PAYMENT_FAILED_STATUSES = ['past_due', 'unpaid', 'incomplete'];

/** Paid through a date in the future. Only these two have a period end worth showing. */
export const PAID_THROUGH_STATUSES = ['active', 'trialing'];

export const needsPayment = (status: string): boolean =>
    PAYMENT_FAILED_STATUSES.includes(status);

export const isPaidThroughADate = (status: string): boolean =>
    PAID_THROUGH_STATUSES.includes(status);

/**
 * A subscription the member can still act on — cancel, or replace the card. Deliberately wider than
 * {@link isPaidThroughADate}: the whole point is that a failing subscription is still theirs to
 * manage. `canceled`, `incomplete_expired` and anything unrecognised fall out.
 */
export const isManageable = (status: string): boolean =>
    isPaidThroughADate(status) || needsPayment(status) || status === 'paused';

/**
 * Stripe's status in Slovak. Everything user-facing here is Slovak, and the badge was rendering the
 * raw API value in capitals — fine while `active` was the only value that ever arrived, and a bare
 * "PAST_DUE" on the member's own billing screen now that a failing charge is reported. Anything
 * unmapped falls back to the raw value rather than to a guess.
 */
export const STATUS_LABELS: Record<string, string> = {
    active: 'AKTÍVNE',
    trialing: 'SKÚŠOBNÉ OBDOBIE',
    past_due: 'NEUHRADENÉ',
    unpaid: 'NEUHRADENÉ',
    incomplete: 'NEDOKONČENÉ',
    incomplete_expired: 'NEDOKONČENÉ',
    paused: 'POZASTAVENÉ',
    canceled: 'ZRUŠENÉ',
    one_time: 'JEDNORAZOVÉ',
};

export const statusLabel = (status: string): string =>
    STATUS_LABELS[status] || status.toUpperCase();
